import type { DragEndEvent } from "@dnd-kit/core"
import { DndContext, PointerSensor, useDraggable, useSensor, useSensors } from "@dnd-kit/core"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import * as React from "react"
import { Menu, X } from "react-feather"

import { cn } from "@/lib/utils"

interface DraggableDialogContentProps
  extends Omit<React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>, "onDrag"> {
  /**
   * ドラッグハンドルを表示するかどうか
   * @default true
   */
  showDragHandle?: boolean
  /**
   * 閉じるボタンを表示するかどうか
   * @default true
   */
  showCloseButton?: boolean
  /**
   * 閉じるボタンがクリックされた時のコールバック
   */
  onClose?: () => void
}

/**
 * ドラッグ可能なダイアログの内部コンテンツ
 */
function DraggableContent({
  className,
  children,
  showDragHandle,
  showCloseButton,
  onClose,
  contentRef,
  contentProps,
  offset,
}: {
  className?: string
  children: React.ReactNode
  showDragHandle: boolean
  showCloseButton: boolean
  onClose?: () => void
  contentRef: React.Ref<HTMLDivElement>
  contentProps: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
  offset: { x: number; y: number }
}) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: "dialog-draggable",
  })

  // ドラッグ中のtransformと、累積オフセットを計算
  const dragX = transform ? transform.x : 0
  const dragY = transform ? transform.y : 0

  const style: React.CSSProperties = {
    // left: 50%, top: 50% からの位置調整
    // -50% で中央配置 + ドラッグ移動量 + 累積オフセット
    transform: `translate(calc(-50% + ${dragX + offset.x}px), calc(-50% + ${dragY + offset.y}px))`,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("fixed left-1/2 top-1/2 z-50", "w-full max-w-lg")}
    >
      <DialogPrimitive.Content
        ref={contentRef}
        className={cn(
          "grid w-full gap-4 border bg-background shadow-lg sm:rounded-lg",
          "duration-200",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          className
        )}
        {...contentProps}
      >
        {/* ドラッグハンドルヘッダー */}
        {(showDragHandle || showCloseButton) && (
          <div
            className="flex items-center justify-between border-b bg-slate-50 px-4 py-2 cursor-move rounded-t-lg touch-none"
            {...listeners}
            {...attributes}
          >
            <div className="flex-1" />
            {showDragHandle && (
              <div className="flex justify-center">
                <Menu className="h-5 w-5 text-slate-400" />
              </div>
            )}
            <div className="flex-1 flex justify-end">
              {showCloseButton && (
                <DialogPrimitive.Close
                  className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onClick={onClose}
                  aria-label="閉じる"
                >
                  <X className="h-4 w-4" />
                </DialogPrimitive.Close>
              )}
            </div>
          </div>
        )}
        {children}
      </DialogPrimitive.Content>
    </div>
  )
}

/**
 * ドラッグ可能なダイアログコンテンツコンポーネント
 *
 * ヘッダー部分のドラッグハンドルをドラッグすることでダイアログを移動できます。
 */
const DraggableDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DraggableDialogContentProps
>(
  (
    { className, children, showDragHandle = true, showCloseButton = true, onClose, ...props },
    ref
  ) => {
    const [offset, setOffset] = React.useState({ x: 0, y: 0 })

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 5,
        },
      })
    )

    const handleDragEnd = React.useCallback((event: DragEndEvent) => {
      const { delta } = event
      setOffset((prev) => ({
        x: prev.x + delta.x,
        y: prev.y + delta.y,
      }))
    }, [])

    return (
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <DraggableContent
            className={className}
            showDragHandle={showDragHandle}
            showCloseButton={showCloseButton}
            onClose={onClose}
            contentRef={ref}
            contentProps={props}
            offset={offset}
          >
            {children}
          </DraggableContent>
        </DndContext>
      </DialogPrimitive.Portal>
    )
  }
)
DraggableDialogContent.displayName = "DraggableDialogContent"

export { DraggableDialogContent }
export type { DraggableDialogContentProps }
